import { useCallback, useState } from 'react'
import { SelectOption } from 'components/form/Select/useSelect'
import apiClient from 'helpers/clients/apiClient'
import {
  LocationInfo,
  GEOLOCATION_TIMEOUT,
  GEOLOCATION_MAX_AGE,
  createCoordinatesData,
  createSortingInfo,
} from '../utils'

export const useLocationManagement = (
  onCoordinatesSelect: (value: any) => void,
  setIsModalOpen: (open: boolean) => void,
) => {
  const [locationLoading, setLocationLoading] = useState(false)
  const [currentLocation, setCurrentLocation] = useState<LocationInfo | undefined>(undefined)

  const handleUseMyLocationClick = useCallback(() => {
    if (!navigator?.geolocation) {
      setLocationLoading(false)
      return
    }

    setLocationLoading(true)
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const { latitude, longitude } = coords
        const locationInfo = {
          error: false,
          lat: latitude,
          long: longitude,
        }

        setCurrentLocation(locationInfo)
        handleRedirect(locationInfo, undefined)
      },
      (error) => {
        console.error('Error getting location:', error)
        setCurrentLocation({
          error: true,
          lat: undefined,
          long: undefined,
        })
        setLocationLoading(false)
      },
      { maximumAge: GEOLOCATION_MAX_AGE, timeout: GEOLOCATION_TIMEOUT },
    )
  }, [])

  const handleRedirect = useCallback(
    async (currentLocation: LocationInfo | undefined, selectedOption: SelectOption | undefined) => {
      const coordinatesData = createCoordinatesData(currentLocation, selectedOption)

      if (!coordinatesData) return

      try {
        const response = await apiClient.setCustomerCoordinates(coordinatesData)
        if (response.success && response.data) {
          const sortingInfo = createSortingInfo(coordinatesData)
          onCoordinatesSelect(sortingInfo)
          setIsModalOpen(false)
        }
      } catch (error) {
        console.error('Error setting coordinates:', error)
      }
    },
    [onCoordinatesSelect, setIsModalOpen],
  )

  const resetLocationState = useCallback(() => {
    setLocationLoading(false)
    setCurrentLocation(undefined)
  }, [])

  return {
    locationLoading,
    currentLocation,
    handleUseMyLocationClick,
    handleRedirect,
    resetLocationState,
  }
}
